'use client';
import React from 'react';
import { UserOutlined, DownOutlined } from '@ant-design/icons';
import styles from './AccountMenu.module.scss';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@benzinga/core-ui';
import { useHydrate, useLocalStorage } from '@benzinga/hooks';
import { useIsUserEditor } from '@benzinga/user-context';
import { getRegisterType } from '@benzinga/utils';
import classNames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { getPremiumServiceHref } from '../premiumServiceLinks';
import { clearNavChromeForLogout } from '../navChromeBoot';
import { usePremiumServiceAccess } from '../hooks/usePremiumServiceAccess';

interface AccountMenuProps {
  isLoggedIn: boolean;
  onLogout: () => void;
  deviceType: 'mobile' | 'desktop';
  navChromeReady?: boolean;
}

interface AccountMenuItemProps {
  item: {
    boot?: string;
    className?: string;
    label: string;
    href?: string;
  };
}

export const AccountMenu: React.FC<AccountMenuProps> = ({ deviceType, isLoggedIn, navChromeReady = true, onLogout }) => {
  const { t } = useTranslation('common');
  const [isAccountMenuOpen, setIsAccountMenuOpen] = React.useState(false);
  const [isEditorModeEnabled, setIsEditorModeEnabled] = useLocalStorage('editor_mode_enabled', false);
  const isEditor = useHydrate(useIsUserEditor(), false);
  const premiumAccess = usePremiumServiceAccess(isLoggedIn);
  const { hasEdge: showEdge, hasPro: showPro, hasResearch: showResearch } = premiumAccess;
  const showServicesDivider = showPro || showEdge || showResearch;

  const handleToggleEditorMode = () => {
    setIsEditorModeEnabled(!isEditorModeEnabled);
  };

  const items = [
    {
      children: (
        <Link href="/account" prefetch={false}>
          {t('Navigation.AccountMenu.my-account-text')}
        </Link>
      ),
      hidden: !isLoggedIn,
      label: t('Navigation.AccountMenu.my-account-text'),
    },
    {
      hidden: !isLoggedIn,
      href: 'https://www.benzinga.com/profile/portfolio/?action=notifications',
      label: 'Notifications',
    },
    {
      hidden: !isLoggedIn,
      href: 'https://www.benzinga.com/profile/portfolio',
      label: 'Overview',
    },
    {
      hidden: !isLoggedIn,
      href: 'https://www.benzinga.com/profile/portfolio/?action=new_watchlist',
      label: '+ New Watchlist',
    },
    {
      hidden: !isLoggedIn,
      label: 'DIVIDER',
    },
    {
      children: (
        <Checkbox
          checked={isEditorModeEnabled}
          className="flex"
          id={`admin-mode_${deviceType}`}
          label="Admin Mode"
          onChange={handleToggleEditorMode}
        />
      ),
      hidden: !isLoggedIn || (isLoggedIn && !isEditor),
      label: 'Admin Mode',
    },
    {
      hidden: !isLoggedIn || !isEditor,
      label: 'DIVIDER',
    },
    {
      children: (
        <span className="service-label">
          Benzinga <span className="service-keyword">Edge</span>
        </span>
      ),
      boot: 'svc-edge',
      className: 'benzinga-edge',
      hidden: navChromeReady && !showEdge,
      href: getPremiumServiceHref('edge', premiumAccess),
      label: 'Benzinga Edge',
    },
    {
      children: (
        <span className="service-label">
          Benzinga <span className="service-keyword">Research</span>
        </span>
      ),
      boot: 'svc-research',
      className: 'benzinga-plus',
      hidden: navChromeReady && !showResearch,
      href: getPremiumServiceHref('research', premiumAccess),
      label: 'Benzinga Research',
    },
    {
      children: (
        <span className="service-label">
          Benzinga <span className="service-keyword">Pro</span>
        </span>
      ),
      boot: 'svc-pro',
      className: 'benzinga-pro',
      hidden: navChromeReady && !showPro,
      href: getPremiumServiceHref('pro', premiumAccess),
      label: 'Benzinga Pro',
    },
    {
      boot: 'svc-divider',
      hidden: navChromeReady && !showServicesDivider,
      label: 'DIVIDER',
    },
  ];

  const handleToggleAccountMenu = () => {
    setIsAccountMenuOpen(isOpen => !isOpen);
  };

  const routerAsPath = usePathname();

  const login = React.useCallback(() => {
    const redirectUrl = new URL(`${window.location.origin}/login?action=login&next=${window.location.href}`);

    const utm_source = getRegisterType(routerAsPath ?? '');
    if (utm_source) {
      redirectUrl.searchParams.append('utm_source', utm_source);
    }

    window.location.href = redirectUrl.toString();
  }, [routerAsPath]);

  return (
    <div className={`${styles.menuWrapper} account-menu menu-wrapper`}>
      <button className="menu-button" onClick={handleToggleAccountMenu}>
        <UserOutlined />
        <span className="my-account-text">{t('Navigation.AccountMenu.my-account-text')}</span>
        <DownOutlined />
      </button>
      <div className={classNames('account-menu-dropdown', { open: isAccountMenuOpen })}>
        {items.map((item, index) => {
          if (item?.hidden) return null;
          const boot = 'boot' in item ? item.boot : undefined;
          if (item.label === 'DIVIDER') {
            return <hr className="divider" data-nav-boot={boot} key={`${item.label}-${index}`} />;
          }
          return (
            <AccountMenuItem item={{ ...item, boot }} key={`${item.label}-${index}`}>
              {item.children || item.label}
            </AccountMenuItem>
          );
        })}
        {isLoggedIn ? (
          <div
            className="account-menu-item"
            onClick={() => {
              clearNavChromeForLogout();
              onLogout();
            }}
          >
            <span>{t('Navigation.AccountMenu.logout')}</span>
          </div>
        ) : (
          <div className="account-menu-item">
            <button className="auth-link-button" onClick={login}>
              {t('Navigation.TopBar.login')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AccountMenuItem: React.FC<React.PropsWithChildren<AccountMenuItemProps>> = ({ children, item }) => {
  const className = `account-menu-item${item.className ? ` ${item.className}` : ''}`;
  return (
    <div className={className} data-nav-boot={item.boot}>
      {item.href ? <a href={item.href}>{children}</a> : <span>{children}</span>}
    </div>
  );
};
